/**
 * Trunnion AI - User Management
 * Operator CRUD interface.
 */

import { useState } from 'react';
import { Plus, Users, Trash2, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import type { Operator, ClassificationLevel } from '@/types';

export function UserManagement() {
  const [users] = useState<Operator[]>([]);
  const [createOpen, setCreateOpen] = useState(false);

  const columns: Column<Operator>[] = [
    {
      key: 'name',
      header: 'User',
      sortable: true,
      render: (u) => (
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-trunnion-gold-500/10 text-xs font-bold text-trunnion-gold-500">
            {u.displayName.charAt(0).toUpperCase()}
          </div>
          <div>
            <span className="text-white font-medium">{u.displayName}</span>
            <p className="text-xs text-trunnion-slate-400">{u.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (u) => (
        <Badge variant={u.role === 'admin' ? 'warning' : 'default'}>
          {u.role}
        </Badge>
      ),
    },
    {
      key: 'clearance',
      header: 'Clearance',
      render: (u) => <ClassificationTag level={u.clearanceLevel as ClassificationLevel} />,
    },
    {
      key: 'org',
      header: 'Organization',
      render: (u) => (
        <span className="text-trunnion-slate-300">{u.organization}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (u) => (
        <Badge variant={u.isActive ? 'success' : 'default'} dot>
          {u.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      render: (u) => (
        <span className="text-xs text-trunnion-slate-400">
          {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="rounded p-1.5 text-trunnion-slate-400 hover:bg-trunnion-slate-700 hover:text-white">
            <Edit2 className="h-3.5 w-3.5" />
          </button>
          <button className="rounded p-1.5 text-trunnion-slate-400 hover:bg-trunnion-slate-700 hover:text-red-400">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">
          <Users className="h-5 w-5 inline mr-2 text-trunnion-gold-500" />
          User Management
        </h2>
        <Button
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setCreateOpen(true)}
        >
          Add User
        </Button>
      </div>

      <DataTable<Operator>
        columns={columns}
        data={users}
        keyExtractor={(u) => u.id}
        emptyMessage="No users found"
      />

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add User"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => setCreateOpen(false)}>Create User</Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input label="Username" placeholder="johndoe" />
          <Input label="Display Name" placeholder="John Doe" />
          <Input label="Email" type="email" placeholder="john@example.com" />
          <Select
            label="Role"
            options={[
              { value: 'admin', label: 'Admin' },
              { value: 'operator', label: 'Operator' },
              { value: 'analyst', label: 'Analyst' },
              { value: 'auditor', label: 'Auditor' },
              { value: 'viewer', label: 'Viewer' },
            ]}
          />
          <Input label="Organization" placeholder="Organization name" />
          <Input label="Password" type="password" placeholder="Minimum 12 characters" />
        </div>
      </Modal>
    </div>
  );
}
